
import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram } from 'lucide-react';
import acaoDigitalLogo from '@/assets/logo-acao-digital-marketing.png';
import deluxeLogo from '@/assets/logo-projeto-deluxe.png';
import { useBooking } from '@/contexts/BookingContext';
import procedimentos from '@/data/procedimentos.json';

// Mesma lista do menu, dos cards da home, do formulário e do sitemap.
const [mamarias, corporal, faciais] = procedimentos.map((g) => g.items);

const NavCol: React.FC<{ label: string; items: { name: string; slug: string }[] }> = ({ label, items }) => (
  <div>
    <p className="text-[10px] font-semibold text-light-primary uppercase tracking-widest mb-4">{label}</p>
    <ul className="space-y-2.5">
      {items.map(p => (
        <li key={p.slug}>
          <Link to={`/procedimentos/${p.slug}`} className="link-muted">
            {p.name}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const Footer: React.FC = () => {
  const { handleWhatsAppOpen } = useBooking();
  return (
  <>
    <footer aria-labelledby="footer-heading" className="bg-dark-elevated border-t border-white/5 pt-14 pb-10 md:pt-16 md:pb-12">
      <h2 id="footer-heading" className="sr-only">Rodapé</h2>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Main grid */}
        <nav aria-label="Navegação do rodapé" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[1.4fr_1fr_1fr_1fr] gap-10 md:gap-8">

          {/* ── Col 1: Brand ── */}
          <div className="flex flex-col items-center text-center md:items-start md:text-left">
            <div className="mb-5 inline-flex flex-col items-center leading-none">
              <img
                src="/simbolo-luis-brunello-sm.webp"
                alt="Logo Dr. Luis Brunello — Cirurgião Plástico em Curitiba"
                title="Dr. Luis Brunello — Cirurgia Plástica"
                className="h-14 w-auto object-contain mb-3"
                width="47" height="112"
                loading="lazy" decoding="async"
              />
              <span className="font-display text-light-primary text-2xl font-normal uppercase" style={{ letterSpacing: '0.12em' }}>
                Luis Brunello
              </span>
              <span className="text-light-muted text-xs tracking-[0.28em] uppercase mt-1.5" style={{ fontFamily: "'area-normal', sans-serif" }}>
                Cirurgia Plástica
              </span>
            </div>

            <div className="space-y-1.5 text-sm text-light-muted mb-4">
              <p className="font-medium text-light-secondary">CRM-PR 40.350 | RQE 38.853</p>
              <p>Curitiba — PR</p>
            </div>

            <p className="text-sm text-light-muted leading-relaxed mb-6 max-w-xs">
              Especialista em cirurgia plástica corporal com planejamento individualizado e acompanhamento próximo em todas as etapas da jornada do paciente.
            </p>

            {/* Social + WhatsApp */}
            <div className="flex items-center gap-3">
              <a
                href="https://www.instagram.com/luisbrunello/"
                target="_blank" rel="noopener noreferrer"
                className="bg-light-primary text-dark-base rounded-full w-10 h-10 flex items-center justify-center hover:bg-light-secondary transition-colors"
                aria-label="Instagram do Dr. Luis Brunello"
              >
                <Instagram className="h-4 w-4" aria-hidden="true" />
              </a>
              <button
                onClick={() => handleWhatsAppOpen()}
                className="bg-light-primary text-dark-base rounded-full w-10 h-10 flex items-center justify-center hover:bg-light-secondary transition-colors"
                aria-label="Agendar consulta via WhatsApp"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                  <path d="M12 0C5.373 0 0 5.373 0 12c0 2.115.549 4.103 1.512 5.83L.057 23.486a.5.5 0 0 0 .609.61l5.805-1.442A11.943 11.943 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.807 9.807 0 0 1-5.012-1.375l-.36-.214-3.724.925.966-3.619-.234-.373A9.787 9.787 0 0 1 2.182 12C2.182 6.57 6.57 2.182 12 2.182S21.818 6.57 21.818 12 17.43 21.818 12 21.818z"/>
                </svg>
              </button>
            </div>
          </div>

          {/* ── Col 2: Navegação + Links ── */}
          <div className="space-y-8">
            <div>
              <p className="text-[10px] font-semibold text-light-primary uppercase tracking-widest mb-4">Navegação</p>
              <ul className="space-y-2.5">
                {[
                  { label: 'Sobre o Dr. Luis Brunello', to: '/sobre' },
                  { label: 'Jornada do Paciente', to: '/jornada' },
                  { label: 'Blog', to: '/blog' },
                  { label: 'Glossário', to: '/glossario' },
                ].map(link => (
                  <li key={link.to}>
                    <Link to={link.to} className="link-muted">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <NavCol label="Cirurgias Mamárias" items={mamarias} />
          </div>

          {/* ── Col 3: Contorno Corporal ── */}
          <NavCol label="Contorno Corporal" items={corporal} />

          {/* ── Col 4: Faciais e Complementares ── */}
          <NavCol label="Faciais e Complementares" items={faciais} />

        </nav>

        {/* Legal */}
        <div className="border-t border-white/5 mt-12 pt-8 space-y-4">
          <p className="text-light-muted text-xs leading-relaxed text-center max-w-4xl mx-auto">
            O conteúdo deste site é informativo e educativo, elaborado com base na experiência clínica do Dr. Luis Brunello, médico especialista em cirurgia plástica, CRM-PR 40.350 | RQE 38.853, e segue as normas do Conselho Federal de Medicina.
          </p>
          <p className="text-light-muted text-xs text-center">
            © {new Date().getFullYear()} Dr. Luis Brunello. Todos os direitos reservados.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/contato" className="text-light-muted text-xs hover:text-gold transition-colors">Contato</Link>
            <span className="text-white/10">·</span>
            <Link to="/politica-de-privacidade" className="text-light-muted text-xs hover:text-gold transition-colors">Política de Privacidade</Link>
            <span className="text-white/10">·</span>
            <Link to="/termos-de-uso" className="text-light-muted text-xs hover:text-gold transition-colors">Termos de Uso</Link>
            <span className="text-white/10">·</span>
            <Link to="/mapa-do-site" className="text-light-muted text-xs hover:text-gold transition-colors">Mapa do site</Link>
          </div>
        </div>
      </div>
    </footer>

    <div className="w-full bg-dark-base py-4 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 text-light-muted text-xs sm:text-sm leading-tight">
          <span className="whitespace-nowrap">Este site foi desenvolvido pela</span>
          {/* Créditos sitewide → nofollow: link de rodapé em todas as páginas não deve passar autoridade. */}
          <a href="https://www.instagram.com/acaodigitalmarketing/" target="_blank" rel="noopener noreferrer nofollow" className="hover:opacity-80 transition-opacity flex-shrink-0">
            <img src={acaoDigitalLogo} alt="Logo Ação Digital Marketing" title="Ação Digital Marketing" className="h-4 sm:h-5 lg:h-6 w-auto brightness-0 invert opacity-70" loading="lazy" decoding="async" width="120" height="24" />
          </a>
          <span className="whitespace-nowrap">como parte das entregas do projeto</span>
          <a href="https://www.analuizavianah.com.br/" target="_blank" rel="noopener noreferrer nofollow" className="hover:opacity-80 transition-opacity flex-shrink-0">
            <img src={deluxeLogo} alt="Logo Deluxe — Projeto Ana Luiza Vianna" title="Projeto Deluxe" className="h-4 sm:h-5 lg:h-6 w-auto brightness-0 invert opacity-70" loading="lazy" decoding="async" width="120" height="24" />
          </a>
        </div>
      </div>
    </div>
  </>
  );
};

export default Footer;
